import { useStreamingReport } from '../../hooks/useStreamingReport';
import StreamingStatus from '../../components/StreamingStatus';
import React, { useEffect, useMemo } from 'react';
import { Button, Form, Input, Select, ConfigProvider, message, Checkbox, Tooltip, Radio } from 'antd';
import { AppstoreOutlined, ArrowRightOutlined, MinusCircleOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/en';
import { SolarDay, SolarTime } from 'tyme4ts';
import { Solar } from 'lunar-typescript';
import { useIztro } from 'iztro-hook';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate } from 'react-router-dom';

import '../../App.css';
import BirthDateTimePicker from '../../components/BirthDateTimePicker';
import FourPillars from '../../components/FourPillars';
import AILoadingAnimation from '../../components/AILoadingAnimation';
import { cityFilterOption, findCityByOptionValue, getCityOptionsByLocale, type City } from '../../data/cities';
import { ALL_PRODUCTS, getProductsByWuxing, type ProductItem } from '../../data/products';
import { calculateDaYunWithAge, type DaYunWithAge, ZIWEI_CONFIG, buildZiWeiData } from '../../utils';
import { useExportImage } from '../../hooks/useExportImage';
import ShareCard from '../../components/ShareCard';
import { getLiteCopy, LITE_LOCALE_STORAGE_KEY, type LiteLocale } from './i18n';

dayjs.locale('zh-cn');
dayjs.extend(dayOfYear);

const DEFAULT_ZIWEI_BIRTHDAY = '2000-1-1';
const BAZI_LITE_USER_ID_STORAGE_KEY = 'bazi-lite:user-id:v1';

interface InlineRecommendationMeta {
  slot: string;
  productId: number;
  title: string;
  reason: string;
}

interface WuxingStructuredData {
  xiYongWuXing: string;
  secondaryXiYongWuXing?: string[];
  avoidWuXing?: string[];
  conditionalWuXing?: Array<{ element: string; condition: string }>;
  verificationSummary?: string;
  xiYongConfidence?: string;
  xiYongJudgeMode?: string;
  xiYongDisagreement?: string;
  recommendedProductIds?: number[];
  inlineRecommendations?: InlineRecommendationMeta[];
}

interface PastEventFormItem {
  year?: number;
  event?: string;
}

interface BaziLiteHistoryRecord {
  recordId: string;
  createdAt: string;
  birthSolar: string;
  cityName: string;
  gender: string;
  question: string;
  validationAnswer?: string;
  validationFeedback?: string;
  xiYongDecision?: unknown;
  answer?: string;
  hasAnswer?: boolean;
}

interface ShareSummaryData {
  gender: string;
  birthInfo: string;
  personalityTags: string[];
  yearKeywords: string[];
  oneLiner: string;
  year: number;
  focusLabel: string;
}

interface FormValues {
  birthDateTime: {
    date: dayjs.Dayjs;
    isLunar: boolean;
    hourUnknown?: boolean;
    minuteUnknown?: boolean;
  };
  gender: 'male' | 'female';
  cityScope?: 'domestic' | 'overseas';
  question?: string;
  pastEvents?: PastEventFormItem[];
  city?: string;
  longitude: number;
  latitude?: number;
  distinguishZiShi?: boolean;
}

function getBaziLiteUserId() {
  if (typeof window === 'undefined') return '';
  const existing = window.localStorage.getItem(BAZI_LITE_USER_ID_STORAGE_KEY);
  if (existing) return existing;
  const next = `bazi_lite_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  window.localStorage.setItem(BAZI_LITE_USER_ID_STORAGE_KEY, next);
  return next;
}

function parseValidationItems(text: string) {
  const normalized = text
    .replace(/\r/g, '\n')
    .replace(/^#+\s*前事验证.*$/gm, '')
    .trim();
  const matches = [...normalized.matchAll(/(?:^|\s)(\d+)[.、]\s*([\s\S]*?)(?=(?:\s\d+[.、]\s*)|$)/g)];
  if (matches.length > 1) {
    return matches.map((match) => match[2].replace(/\s+/g, ' ').replace(/[。；;]\s*$/, '').trim()).filter(Boolean);
  }
  return normalized
    .split('\n')
    .map((line) => line.replace(/^\d+[.、]\s*/, '').trim())
    .filter((item) => item && !item.startsWith('#'));
}

const PERSONALITY_TAG_PATTERNS = [
  { tag: '心软但有锋芒', regex: /(敏感|细腻|感受力强|共情|心思细)/ },
  { tag: '越到后面越稳', regex: /(坚韧|韧性|扛事|撑住|不服输)/ },
  { tag: '脑子清醒', regex: /(理性|克制|冷静|清醒|讲逻辑)/ },
  { tag: '先观察再出手', regex: /(慢热|谨慎|防备心|不轻易|先观察)/ },
  { tag: '很能扛事', regex: /(责任感|有担当|扛责任|操心|顾全大局)/ },
  { tag: '骨子里不服输', regex: /(独立|要强|不想麻烦别人|靠自己|自立)/ },
  { tag: '直觉在线', regex: /(直觉|第六感|预感|洞察力)/ },
  { tag: '容易想太多', regex: /(内耗|自我怀疑|想太多|反复琢磨|拧巴)/ },
  { tag: '一旦想通就很能冲', regex: /(执行力|行动力|说干就干|推进力)/ },
  { tag: '感情里很认真', regex: /(重感情|讲情义|放不下|念旧)/ },
];

const YEAR_KEYWORD_PATTERNS = [
  { tag: '先稳后发', regex: /(蓄力|积累|打底|沉下来)/ },
  { tag: '准备破局', regex: /(破局|突围|打开局面|松动)/ },
  { tag: '重新校准', regex: /(调整|修整|校准|收回来)/ },
  { tag: '先守住底盘', regex: /(守成|稳住|保守|别冒进)/ },
  { tag: '主动争取', regex: /(进攻年|主动出击|往前冲|加速)/ },
  { tag: '别急着硬冲', regex: /(防守年|先稳住|避坑|别硬冲)/ },
  { tag: '有人会推你一把', regex: /(贵人|有人带|被看见|有人扶)/ },
  { tag: '合作有戏', regex: /(合作|搭档|联手|协同)/ },
  { tag: '更容易被看见', regex: /(曝光|出圈|被看到|表现自己)/ },
  { tag: '财务先稳再扩', regex: /(守财|现金流|财库|稳财)/ },
  { tag: '关系会有答案', regex: /(桃花|感情机会|关系推进|正缘)/ },
  { tag: '先把状态养回来', regex: /(疗愈|休息|修复|恢复元气)/ },
];

function stripMarkdown(rawText: string): string {
  return rawText
    .replace(/\[\[PRODUCT_\d+\]\]/g, ' ')
    .replace(/<!--DATA_START-->[\s\S]*?<!--DATA_END-->/g, ' ')
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^>\s?/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function extractSection(content: string, startHeading: string, nextHeading?: string): string {
  const startIndex = content.indexOf(startHeading);
  if (startIndex === -1) return '';

  const sectionStart = startIndex + startHeading.length;
  const endIndex = nextHeading ? content.indexOf(nextHeading, sectionStart) : -1;
  return content.slice(sectionStart, endIndex === -1 ? undefined : endIndex).trim();
}

function extractFirstSentence(text: string): string {
  const normalized = stripMarkdown(text).replace(/\s+/g, ' ').trim();
  if (!normalized) return '';
  const sentence = normalized.split(/[。！？!?\n]/).map((item) => item.trim()).find(Boolean) || '';
  return sentence.slice(0, 48);
}

function collectTags(text: string, patterns: Array<{ tag: string; regex: RegExp }>, fallback: readonly string[]): string[] {
  const hits = patterns.filter((item) => item.regex.test(text)).map((item) => item.tag);
  const merged = [...hits, ...fallback];
  return Array.from(new Set(merged)).slice(0, 3);
}

function inferQuestionFocus(
  question: string | undefined,
  copy: {
    defaultFocus: string;
    careerFocus: string;
    relationshipFocus: string;
    wealthFocus: string;
    healthFocus: string;
    generalFocus: string;
  }
): string {
  const text = (question || '').trim();
  if (!text) return copy.defaultFocus;
  if (/(工作|事业|跳槽|创业|升职|合作|副业|work|career|job|promotion|startup|business|side hustle|side-hustle|collaboration)/i.test(text)) return copy.careerFocus;
  if (/(感情|关系|复合|结婚|对象|桃花|love|relationship|marriage|partner|dating|romance)/i.test(text)) return copy.relationshipFocus;
  if (/(钱|财|收入|投资|现金流|存款|money|wealth|income|invest|investment|cash flow|savings|finance|financial)/i.test(text)) return copy.wealthFocus;
  if (/(累|情绪|焦虑|失眠|状态|健康|内耗|health|energy|anxiety|emotion|sleep|burnout|stress|wellness)/i.test(text)) return copy.healthFocus;
  return copy.generalFocus;
}

function buildShareOneLiner(
  question: string | undefined,
  yearKeywords: string[],
  yearlySection: string,
  closingSection: string,
  copy: {
    defaultFocus: string;
    careerFocus: string;
    relationshipFocus: string;
    wealthFocus: string;
    healthFocus: string;
    generalFocus: string;
    oneLinerPrefix: string;
    closingPrefixRegex: string;
    careerOneLiner: string;
    relationshipOneLiner: string;
    wealthOneLiner: string;
    healthOneLiner: string;
    actionOneLiner: string;
    defaultYearKeyword: string;
    generalOneLinerPrefix: string;
    generalOneLinerSuffix: string;
  }
) {
  const focusLabel = inferQuestionFocus(question, copy);
  const closingSentence = extractFirstSentence(closingSection);

  if (closingSentence) {
    const cleanSentence = closingSentence.replace(new RegExp(copy.closingPrefixRegex, 'i'), '');
    return {
      focusLabel,
      oneLiner: `${focusLabel}${localePunctuation(cleanSentence)}${copy.oneLinerPrefix}：${cleanSentence}${/[。！？.!?]$/.test(cleanSentence) ? '' : localeSentenceEnd(copy)}`,
    };
  }

  const text = (question || '').trim();
  if (/(工作|事业|跳槽|创业|升职|合作|副业|work|career|job|promotion|startup|business|side hustle|side-hustle|collaboration)/i.test(text)) {
    return {
      focusLabel,
      oneLiner: copy.careerOneLiner,
    };
  }

  if (/(感情|关系|复合|结婚|对象|桃花|love|relationship|marriage|partner|dating|romance)/i.test(text)) {
    return {
      focusLabel,
      oneLiner: copy.relationshipOneLiner,
    };
  }

  if (/(钱|财|收入|投资|现金流|存款|money|wealth|income|invest|investment|cash flow|savings|finance|financial)/i.test(text)) {
    return {
      focusLabel,
      oneLiner: copy.wealthOneLiner,
    };
  }

  if (/(累|情绪|焦虑|失眠|状态|健康|内耗|health|energy|anxiety|emotion|sleep|burnout|stress|wellness)/i.test(text)) {
    return {
      focusLabel,
      oneLiner: copy.healthOneLiner,
    };
  }

  if (/进攻|主动|冲|attack|push|move|act|charge|aggressive/i.test(yearlySection)) {
    return {
      focusLabel,
      oneLiner: copy.actionOneLiner,
    };
  }

  return {
    focusLabel,
    oneLiner: `${copy.generalOneLinerPrefix}${yearKeywords[0] || copy.defaultYearKeyword}${copy.generalOneLinerSuffix}`,
  };
}

function localeSentenceEnd(copy: { generalOneLinerPrefix: string }) {
  return /[A-Za-z]/.test(copy.generalOneLinerPrefix) ? '.' : '。';
}

function localePunctuation(text: string) {
  return /[A-Za-z]/.test(text) ? ' ' : '';
}

function buildShareSummary(
  answer: string,
  gender: string,
  birthInfo: string,
  year: number,
  question: string | undefined,
  copy: {
    personalityTags: readonly string[];
    yearKeywords: readonly string[];
    defaultFocus: string;
    careerFocus: string;
    relationshipFocus: string;
    wealthFocus: string;
    healthFocus: string;
    generalFocus: string;
    oneLinerPrefix: string;
    closingPrefixRegex: string;
    careerOneLiner: string;
    relationshipOneLiner: string;
    wealthOneLiner: string;
    healthOneLiner: string;
    actionOneLiner: string;
    defaultYearKeyword: string;
    generalOneLinerPrefix: string;
    generalOneLinerSuffix: string;
  }
): ShareSummaryData {
  const cleanAnswer = stripMarkdown(answer);

  const energySection = extractSection(cleanAnswer, '### 1.1 你的能量底色', '### 1.2');
  const soulSection = extractSection(cleanAnswer, '### 1.3 灵魂的两面', '### 1.4');
  const yearlySection = extractSection(cleanAnswer, '### 3.1 年度关键词', '### 3.2');
  const closingSection = extractSection(cleanAnswer, '## 五、写在最后');

  const personalityTags = collectTags(
    `${energySection}\n${soulSection}`,
    PERSONALITY_TAG_PATTERNS,
    copy.personalityTags
  );

  const yearKeywords = collectTags(
    yearlySection,
    YEAR_KEYWORD_PATTERNS,
    copy.yearKeywords
  );

  const { focusLabel, oneLiner } = buildShareOneLiner(question, yearKeywords, yearlySection, closingSection, copy);

  return {
    gender,
    birthInfo,
    personalityTags,
    yearKeywords,
    oneLiner,
    year,
    focusLabel,
  };
}

function toTrueSolarTimeWithMinutes(datetime: dayjs.Dayjs, longitude: number, timeZoneOffset: number = 8): dayjs.Dayjs {
  const n = datetime.dayOfYear();
  const B = (2 * Math.PI * (n - 81)) / 364;
  const eot = 9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B);
  const zoneLongitude = 15 * timeZoneOffset;
  const deltaLon = 4 * (longitude - zoneLongitude);
  const deltaMinutes = eot + deltaLon;
  return datetime.add(deltaMinutes, 'minute');
}

function hourToShiChenIndex(hour: number): number {
  return Math.floor((hour + 1) / 2);
}

function getShiChenNameByHour(hour: number, locale: LiteLocale) {
  const normalizedHour = ((hour % 24) + 24) % 24;
  const shiChenNames = locale === 'zh-CN'
    ? ['子时', '丑时', '寅时', '卯时', '辰时', '巳时', '午时', '未时', '申时', '酉时', '戌时', '亥时']
    : ['Zi hour', 'Chou hour', 'Yin hour', 'Mao hour', 'Chen hour', 'Si hour', 'Wu hour', 'Wei hour', 'Shen hour', 'You hour', 'Xu hour', 'Hai hour'];
  const index = normalizedHour === 23 ? 0 : Math.floor((normalizedHour + 1) / 2);
  return shiChenNames[index] || (locale === 'zh-CN' ? '未知时辰' : 'Unknown hour');
}

function getShiChenBoundaryHint(standardDatetime: dayjs.Dayjs, trueSolarDatetime: dayjs.Dayjs, locale: LiteLocale) {
  const totalMinutes = trueSolarDatetime.hour() * 60 + trueSolarDatetime.minute();
  const thresholdMinutes = 15;
  const boundaries = [
    { minuteOfDay: 60, prev: '子时', next: '丑时' },
    { minuteOfDay: 180, prev: '丑时', next: '寅时' },
    { minuteOfDay: 300, prev: '寅时', next: '卯时' },
    { minuteOfDay: 420, prev: '卯时', next: '辰时' },
    { minuteOfDay: 540, prev: '辰时', next: '巳时' },
    { minuteOfDay: 660, prev: '巳时', next: '午时' },
    { minuteOfDay: 780, prev: '午时', next: '未时' },
    { minuteOfDay: 900, prev: '未时', next: '申时' },
    { minuteOfDay: 1020, prev: '申时', next: '酉时' },
    { minuteOfDay: 1140, prev: '酉时', next: '戌时' },
    { minuteOfDay: 1260, prev: '戌时', next: '亥时' },
    { minuteOfDay: 1380, prev: '亥时', next: '子时' },
  ];

  for (const boundary of boundaries) {
    const diff = Math.abs(totalMinutes - boundary.minuteOfDay);
    if (diff <= thresholdMinutes) {
      if (locale === 'zh-CN') {
        return `当前真太阳时落在时辰交界前后 ${thresholdMinutes} 分钟内，接近${boundary.prev}/${boundary.next}交界，时柱可能受临界分钟影响。建议把两个时辰都试一下对比结果；如果你很在意定盘准确度，也可以联系我们专业客服咨询定盘服务。`;
      }

      return `Your true solar time falls within ${thresholdMinutes} minutes of the ${boundary.prev}/${boundary.next} boundary, so the hour pillar may be affected by the exact minute. We recommend trying both adjacent shichen values and comparing the results. If chart accuracy matters a lot to you, consider confirming the chart with a professional consultation.`;
    }
  }

  const standardShiChen = getShiChenNameByHour(standardDatetime.hour(), locale);
  const trueSolarShiChen = getShiChenNameByHour(trueSolarDatetime.hour(), locale);
  if (standardShiChen !== trueSolarShiChen) {
    if (locale === 'zh-CN') {
      return `按真太阳时换算后，已经和北京时间跨时辰了：北京时间是${standardShiChen}，真太阳时是${trueSolarShiChen}。这类情况建议你自己确认一下，也可以把两个时辰都试试；如果拿不准，也可以联系我们做定盘。`;
    }

    return `After converting to true solar time, your shichen differs from Beijing time: Beijing time gives ${standardShiChen}, while true solar time gives ${trueSolarShiChen}. In this case, it is worth double-checking and trying both hour options before deciding which chart to use.`;
  }

  return '';
}

function ProductImage({ product }: { product: ProductItem }) {
  const [src, setSrc] = React.useState(product.thumbnail || product.image || product.placeholderImage);

  return (
    <img
      className="inline-product-card-image"
      src={src}
      alt={product.name}
      onError={() => {
        if (src !== product.placeholderImage) {
          setSrc(product.placeholderImage);
        }
      }}
      style={{
        width: '124px',
        height: '124px',
        borderRadius: '16px',
        objectFit: 'cover',
        flexShrink: 0,
        background: '#f6efe4',
        border: '1px solid rgba(212, 175, 106, 0.18)',
        boxShadow: '0 8px 18px rgba(91, 61, 31, 0.08)',
      }}
    />
  );
}

function renderInlineProductCard(
  meta: InlineRecommendationMeta | undefined,
  products: ProductItem[],
  key: string,
  exportMode: boolean,
  copy: {
    productBadge: string;
    productSearchLead: string;
    productSearchHint: string;
  }
) {
  if (!meta) {
    return null;
  }

  const product = products.find((item) => item.id === meta.productId);
  if (!product) {
    return null;
  }

  const cardContent = (
    <>
      <div className="inline-product-card-badge" style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '11px',
        color: '#fff',
        background: 'linear-gradient(135deg, #8b5cf6 0%, #d4af6a 100%)',
        padding: '4px 10px',
        borderRadius: '999px',
        marginBottom: '12px',
        fontWeight: 600
      }}>
        ✨ {copy.productBadge}
      </div>

      <div className="inline-product-card-title" style={{ fontSize: '16px', fontWeight: 700, color: '#333', marginBottom: '8px' }}>
        {meta.title}
      </div>

      <div className="inline-product-card-reason" style={{ fontSize: '13px', color: '#666', lineHeight: 1.8, marginBottom: '14px' }}>
        {meta.reason}
      </div>

      <div className="inline-product-card-shell" style={{
        padding: '18px',
        background: 'rgba(255, 255, 255, 0.82)',
        borderRadius: '12px',
        border: '1px solid rgba(212, 175, 106, 0.18)'
      }}>
        <div className="inline-product-card-content" style={{ display: 'flex', alignItems: 'flex-start', gap: '18px' }}>
          <ProductImage product={product} />
          <div className="inline-product-card-main" style={{ flex: 1 }}>
            <div className="inline-product-card-header" style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
              <div className="inline-product-card-product-name" style={{ fontSize: '16px', fontWeight: 700, color: '#333', lineHeight: 1.5 }}>
                {product.name}
              </div>
              <div className="inline-product-card-price" style={{
                fontSize: '17px',
                color: '#d4af6a',
                fontWeight: 700,
                whiteSpace: 'nowrap'
              }}>
                ¥{product.price}
              </div>
            </div>
            <div className="inline-product-card-desc" style={{ fontSize: '13px', color: '#666', lineHeight: 1.7, marginBottom: '12px' }}>
              {product.selling_point}
            </div>
            {exportMode ? (
              <div
                className="inline-product-card-search-tip"
                style={{
                  padding: '10px 12px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, rgba(212, 175, 106, 0.12) 0%, rgba(139, 92, 246, 0.08) 100%)',
                  border: '1px solid rgba(212, 175, 106, 0.18)',
                }}
              >
                <div style={{ fontSize: '11px', color: '#8a6a3f', marginBottom: '4px', fontWeight: 600 }}>
                  {copy.productSearchLead}
                </div>
                <div style={{ fontSize: '13px', color: '#5b3d1f', fontWeight: 700, lineHeight: 1.6 }}>
                  {copy.productSearchHint}
                </div>
              </div>
            ) : (
              <>
                <div className="inline-product-card-tags" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  {product.tags.map((tag, idx) => (
                    <span
                      key={`${product.id}-${idx}`}
                      className="inline-product-card-tag"
                      style={{
                        fontSize: '11px',
                        color: '#8b5cf6',
                        padding: '4px 10px',
                        background: 'rgba(139, 92, 246, 0.08)',
                        borderRadius: '999px',
                        border: '1px solid rgba(139, 92, 246, 0.16)'
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <Button
                  type="primary"
                  size="small"
                  href={product.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => event.stopPropagation()}
                  style={{
                    height: '34px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #d4af6a 0%, #b8935d 100%)',
                    fontWeight: 700,
                  }}
                >
                  查看商品
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );

  const cardStyle = {
    margin: '22px 0',
    padding: '18px 20px',
    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.06) 0%, rgba(212, 175, 106, 0.10) 100%)',
    borderRadius: '14px',
    border: '1px solid rgba(212, 175, 106, 0.24)',
    boxShadow: '0 8px 20px rgba(212, 175, 106, 0.08)'
  };

  if (!exportMode) {
    return (
      <a
        key={key}
        className="inline-product-card"
        href={product.link}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          ...cardStyle,
          display: 'block',
          color: 'inherit',
          textDecoration: 'none',
          cursor: 'pointer',
        }}
      >
        {cardContent}
      </a>
    );
  }

  return (
    <div
      key={key}
      className="inline-product-card"
      style={cardStyle}
    >
      {cardContent}
    </div>
  );
}

export const LitePage = () => {
  const [form] = Form.useForm();
  const reportRef = React.useRef<HTMLDivElement | null>(null);
  const validationRef = React.useRef<HTMLElement | null>(null);
  const scrollToValidation = React.useRef(false);
  const [locale, setLocale] = React.useState<LiteLocale>(() => {
    if (typeof window === 'undefined') return 'zh-CN';
    return window.localStorage.getItem(LITE_LOCALE_STORAGE_KEY) === 'en-US' ? 'en-US' : 'zh-CN';
  });
  const copy = useMemo(() => getLiteCopy(locale), [locale]);
  const { exportImage } = useExportImage(copy.exportName);
  const navigate = useNavigate();
  const questionValue = Form.useWatch('question', form) || '';
  const birthDateTimeValue = Form.useWatch('birthDateTime', form);
  const hourUnknownSelected = Boolean(birthDateTimeValue?.hourUnknown);
  const [cityScope, setCityScope] = React.useState<'domestic' | 'overseas'>('domestic');

  const [fourPillars, setFourPillars] = React.useState<{ year: string; month: string; day: string; hour: string } | null>(null);
  const [daYun, setDaYun] = React.useState<DaYunWithAge[]>([]);
  const [lunarDateStr, setLunarDateStr] = React.useState<string>('');
  const [trueSolarTimeStr, setTrueSolarTimeStr] = React.useState<string>('');
  const [shiChenBoundaryHint, setShiChenBoundaryHint] = React.useState<string>('');

  const [aiLoading, setAiLoading] = React.useState(false);
  const { run: runReportStream, streaming: reportStreaming, error: streamError } = useStreamingReport();
  const [liteAnswer, setLiteAnswer] = React.useState<string>('');
  const reportHasContent = Boolean(liteAnswer);
  const [currentRecordId, setCurrentRecordId] = React.useState('');
  const [validationAnswer, setValidationAnswer] = React.useState('');
  const [validationFeedbacks, setValidationFeedbacks] = React.useState<Record<number, string>>({});
  const [validationLoading, setValidationLoading] = React.useState(false);
  useEffect(() => {
    if (validationLoading || !validationAnswer || !scrollToValidation.current) return;
    const frame = window.requestAnimationFrame(() => {
      validationRef.current?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        block: 'start',
      });
      scrollToValidation.current = false;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [validationAnswer, validationLoading]);
  const [reportLoading, setReportLoading] = React.useState(false);
  const [isExportingReport, setIsExportingReport] = React.useState(false);
  const [showShareCard, setShowShareCard] = React.useState(false);
  const [recommendedProducts, setRecommendedProducts] = React.useState<ProductItem[]>([]);
  const [inlineRecommendations, setInlineRecommendations] = React.useState<InlineRecommendationMeta[]>([]);
  const [wuxingDataState, setWuxingDataState] = React.useState<WuxingStructuredData | null>(null);
  const [userId, setUserId] = React.useState('');
  const [historyRecords, setHistoryRecords] = React.useState<BaziLiteHistoryRecord[]>([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const validationItems = React.useMemo(() => parseValidationItems(validationAnswer), [validationAnswer]);
  const isValidationComplete = validationItems.length > 0 && validationItems.every((_, index) => validationFeedbacks[index]);
  const wuxingRecommendationBase = React.useMemo(() => {
    if (!wuxingDataState) return '';
    return [
      wuxingDataState.xiYongWuXing,
      ...(Array.isArray(wuxingDataState.secondaryXiYongWuXing) ? wuxingDataState.secondaryXiYongWuXing : []),
    ].filter(Boolean).join('、');
  }, [wuxingDataState]);

  const pastEventYearOptions = React.useMemo(
    () =>
      Array.from({ length: 31 }, (_, index) => {
        const year = 2026 - index;
        return { label: locale === 'zh-CN' ? `${year}年` : `${year}`, value: year };
      }),
    [locale]
  );

  const [ziWeiBirthday, setZiWeiBirthday] = React.useState<string>('');
  const [ziWeiBirthTime, setZiWeiBirthTime] = React.useState<number>(0);
  const [ziWeiGender, setZiWeiGender] = React.useState<'男' | '女'>('女');

  const { astrolabe } = useIztro({
    birthday: ziWeiBirthday || DEFAULT_ZIWEI_BIRTHDAY,
    birthTime: ziWeiBirthTime,
    gender: ziWeiGender,
    birthdayType: 'solar',
    fixLeap: true,
    lang: 'zh-CN',
    ...(ZIWEI_CONFIG as any),
  });

  const ziWeiData = useMemo(() => {
    if (!astrolabe || !ziWeiBirthday || ziWeiBirthday === DEFAULT_ZIWEI_BIRTHDAY) {
      return null;
    }
    return buildZiWeiData(astrolabe, ziWeiBirthTime);
  }, [astrolabe, ziWeiBirthday, ziWeiBirthTime]);

  const cityOptions = React.useMemo(
    () => getCityOptionsByLocale(locale, cityScope),
    [cityScope, locale]
  );

  const handleCityChange = (_cityValue: string, option: any) => {
    const selectedCity = option.city as City;
    form.setFieldsValue({
      longitude: selectedCity.longitude,
      latitude: selectedCity.latitude,
    });
  };

  const handleCityScopeChange = (scope: 'domestic' | 'overseas') => {
    if (cityScope === scope) return;

    setCityScope(scope);
    form.setFieldsValue({
      city: undefined,
      longitude: undefined,
      latitude: undefined,
    });
  };

  const performPaiPan = (birthInfo: {
    year?: number;
    month?: number;
    day?: number;
    hour?: number;
    minute?: number;
    solar?: string;
    longitude: number;
    gender: 'male' | 'female';
    distinguishZiShi?: boolean;
    hourUnknown?: boolean;
  }) => {
    let inputYear: number;
    let inputMonth: number;
    let inputDay: number;
    let inputHour: number;
    let inputMinute: number;

    if (birthInfo.solar) {
      const solarDate = dayjs(birthInfo.solar);
      inputYear = solarDate.year();
      inputMonth = solarDate.month() + 1;
      inputDay = solarDate.date();
      inputHour = solarDate.hour();
      inputMinute = solarDate.minute();
    } else {
      inputYear = birthInfo.year!;
      inputMonth = birthInfo.month!;
      inputDay = birthInfo.day!;
      inputHour = birthInfo.hour!;
      inputMinute = birthInfo.minute!;
    }

    const { longitude, gender, distinguishZiShi = false, hourUnknown = false } = birthInfo;
    const standard = dayjs(
      `${inputYear}-${inputMonth.toString().padStart(2, '0')}-${inputDay
        .toString()
        .padStart(2, '0')} ${inputHour.toString().padStart(2, '0')}:${inputMinute
          .toString()
          .padStart(2, '0')}:00`,
    );
    const trueSolar = hourUnknown ? standard : toTrueSolarTimeWithMinutes(standard, longitude, 8);

    let standardYear = standard.year();
    let standardMonth = standard.month() + 1;
    let standardDay = standard.date();
    const standardHour = standard.hour();
    const standardMinute = standard.minute();

    // ===== 子时处理逻辑 =====
    // 判断是否在子时范围（23:00-01:00）
    const isInZiShi = !hourUnknown && (standardHour === 23 || standardHour === 0);

    if (isInZiShi) {
      if (distinguishZiShi) {
        // 现代计法：区分早晚子时
        if (standardHour === 23) {
          // 早子时（23:00-00:00）：算当天，不调整日期
          console.log('🕐 早子时（23:00-24:00），日柱使用当天');
        } else if (standardHour === 0) {
          // 晚子时（00:00-01:00）：算第二天，日期已经是第二天了，无需调整
          console.log('🕐 晚子时（00:00-01:00），日柱使用第二天');
        }
      } else {
        // 传统计法：23:00-01:00 统一算第二天
        if (standardHour === 23) {
          // 23:00-24:00：需要将日期推进到第二天
          const nextDay = standard.add(1, 'day');
          standardYear = nextDay.year();
          standardMonth = nextDay.month() + 1;
          standardDay = nextDay.date();
          console.log('🕐 传统子时（23:00-24:00），日柱推进到第二天:', `${standardYear}-${standardMonth}-${standardDay}`);
        } else {
          // 00:00-01:00：已经是第二天了，无需调整
          console.log('🕐 传统子时（00:00-01:00），日柱已是第二天');
        }
      }
    }

    const year = trueSolar.year();
    const month = trueSolar.month() + 1;
    const day = trueSolar.date();
    const trueSolarHour = trueSolar.hour();
    const trueSolarMinute = trueSolar.minute();

    setTrueSolarTimeStr(
      hourUnknown
        ? (locale === 'zh-CN' ? '时辰未知，无法换算真太阳时' : 'Birth hour unknown, so true solar time cannot be calculated')
        : trueSolar.format(locale === 'zh-CN' ? 'YYYY年MM月DD日 HH:mm' : 'YYYY-MM-DD HH:mm')
    );
    setShiChenBoundaryHint(
      hourUnknown
        ? (locale === 'zh-CN'
          ? '当前未提供准确出生时辰，时柱会以“**”显示，暂时不能生成完整报告。这里说的“时辰”，就是出生大概落在哪个两小时段里，比如子时（23:00-01:00）、丑时（01:00-03:00）、寅时（03:00-05:00）。如果你知道大概是上午、下午还是晚上，也建议尽量再确认一下具体时辰。'
          : 'You have not provided a confirmed birth hour yet, so the hour pillar will show as “**” and the full report cannot be generated for now. A shichen is a two-hour birth block, such as Zi hour (23:00-01:00), Chou hour (01:00-03:00), or Yin hour (03:00-05:00). If you only know morning, afternoon, or evening, it is still worth trying to confirm the exact birth block.')
        : getShiChenBoundaryHint(standard, trueSolar, locale)
    );

    const solarDayStandard = SolarDay.fromYmd(standardYear, standardMonth, standardDay);
    const lunarDayStandard = solarDayStandard.getLunarDay();
    const solarObj = Solar.fromDate(standard.toDate());
    const lunarObj = solarObj.getLunar();

    const solarHourTrue = hourUnknown ? null : SolarTime.fromYmdHms(year, month, day, trueSolarHour, trueSolarMinute, 0);
    const lunarHourTrue = solarHourTrue?.getLunarHour() || null;

    const sixtyCycleYear = lunarObj.getYearInGanZhiExact();
    let sixtyCycleMonth = lunarDayStandard.getMonthSixtyCycle().getName();
    const sixtyCycleDay = lunarDayStandard.getSixtyCycle().getName();
    const sixtyCycleHour = hourUnknown ? '**' : lunarHourTrue!.getSixtyCycle().getName();

    try {
      const currentJieQi = lunarObj.getCurrentJieQi();

      if (currentJieQi && currentJieQi.isJie()) {
        const jieQiSolar = currentJieQi.getSolar();
        const birthTimestamp = standard.valueOf();
        const jieQiTimestamp = new Date(
          jieQiSolar.getYear(),
          jieQiSolar.getMonth() - 1,
          jieQiSolar.getDay(),
          jieQiSolar.getHour(),
          jieQiSolar.getMinute(),
          jieQiSolar.getSecond(),
        ).getTime();

        if (birthTimestamp < jieQiTimestamp) {
          const prevJieQi = lunarObj.getPrevJieQi();
          if (prevJieQi) {
            const prevJieQiSolar = prevJieQi.getSolar();
            const prevSolar = Solar.fromYmd(
              prevJieQiSolar.getYear(),
              prevJieQiSolar.getMonth(),
              prevJieQiSolar.getDay(),
            );
            sixtyCycleMonth = prevSolar.getLunar().getMonthInGanZhi();
          }
        }
      }
    } catch (error) {
      console.error('月柱节气精确判断失败，使用默认值:', error);
    }

    setLunarDateStr(
      locale === 'zh-CN'
        ? `${lunarObj.getYearInGanZhiExact()}(${lunarObj.getYearShengXiaoExact()})年 ${lunarObj.getMonthInChinese()}月${lunarObj.getDayInChinese()} ${hourUnknown ? '时辰未知' : `${lunarHourTrue!
          .getSixtyCycle()
          .getName()}时`}`
        : `${lunarObj.getYearInGanZhiExact()} (${lunarObj.getYearShengXiaoExact()}) ${lunarObj.getMonthInChinese()} ${lunarObj.getDayInChinese()} ${hourUnknown ? 'hour unknown' : `${lunarHourTrue!
          .getSixtyCycle()
          .getName()} hour`}`,
    );

    setFourPillars({ year: sixtyCycleYear, month: sixtyCycleMonth, day: sixtyCycleDay, hour: sixtyCycleHour });

    const daYunList = calculateDaYunWithAge(
      standardYear,
      standardMonth,
      standardDay,
      standardHour,
      standardMinute,
      sixtyCycleYear,
      sixtyCycleMonth,
      gender,
      8,
    );
    setDaYun(daYunList);

    if (hourUnknown) {
      setZiWeiBirthday(DEFAULT_ZIWEI_BIRTHDAY);
      setZiWeiBirthTime(0);
      setZiWeiGender(gender === 'male' ? '男' : '女');
    } else {
      let validHour = Math.floor(trueSolarHour);
      if (validHour < 0) validHour = 0;
      if (validHour > 23) validHour = 23;

      const birthdayStr = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      const shiChenIndex = hourToShiChenIndex(validHour);

      setZiWeiBirthday(birthdayStr);
      setZiWeiBirthTime(shiChenIndex);
      setZiWeiGender(gender === 'male' ? '男' : '女');
    }
  };

  const onFinish = async (values: FormValues) => {
    const { birthDateTime, longitude, gender, distinguishZiShi } = values;
    if (!birthDateTime) {
      message.error(copy.toast.selectBirth);
      return;
    }

    const { date } = birthDateTime;
    const longitudeNum = Number(longitude);
    if (Number.isNaN(longitudeNum)) {
      message.error(copy.toast.invalidLongitude);
      return;
    }

    const standard = date.second(0).millisecond(0);
    performPaiPan({
      year: standard.year(),
      month: standard.month() + 1,
      day: standard.date(),
      hour: standard.hour(),
      minute: standard.minute(),
      longitude: longitudeNum,
      gender,
      distinguishZiShi: distinguishZiShi || false,
      hourUnknown: Boolean(birthDateTime.hourUnknown),
    });

    setLiteAnswer('');
    setCurrentRecordId('');
    setValidationAnswer('');
    setValidationFeedbacks({});
    setRecommendedProducts([]);
    setInlineRecommendations([]);
    setWuxingDataState(null);
  };

  const applyLiteAnswerPayload = (answer: string) => {
    try {
      const dataStart = answer.indexOf('<!--DATA_START-->');
      const dataEnd = answer.indexOf('<!--DATA_END-->');
      console.log('dataStart', dataStart, 'dataEnd', dataEnd);

      if (dataStart !== -1 && dataEnd !== -1) {
        const jsonStr = answer.substring(dataStart + 15, dataEnd).trim();
        console.log('📝 原始JSON字符串:', jsonStr);

        let cleanedJsonStr = jsonStr;
        if (cleanedJsonStr.startsWith('{') === false) {
          const firstBrace = cleanedJsonStr.indexOf('{');
          const lastBrace = cleanedJsonStr.lastIndexOf('}');
          if (firstBrace !== -1 && lastBrace !== -1) {
            cleanedJsonStr = cleanedJsonStr.substring(firstBrace, lastBrace + 1);
          }
        }

        console.log('🔧 清理后的JSON字符串:', cleanedJsonStr);

        const wuxingData = JSON.parse(cleanedJsonStr);
        console.log('✅ 解析成功:', wuxingData);
        const normalizedWuxingData: WuxingStructuredData = {
          ...wuxingData,
          verificationSummary: wuxingData.verificationSummary || '',
        };

        localStorage.setItem('bazi_wuxing_data', JSON.stringify({
          xiYongWuXing: normalizedWuxingData.xiYongWuXing,
          secondaryXiYongWuXing: normalizedWuxingData.secondaryXiYongWuXing,
          avoidWuXing: normalizedWuxingData.avoidWuXing,
          conditionalWuXing: normalizedWuxingData.conditionalWuXing,
          verificationSummary: normalizedWuxingData.verificationSummary,
          recommendedProductIds: normalizedWuxingData.recommendedProductIds,
          timestamp: Date.now()
        }));
        setWuxingDataState(normalizedWuxingData);
        setInlineRecommendations(
          Array.isArray(normalizedWuxingData.inlineRecommendations) ? normalizedWuxingData.inlineRecommendations : []
        );

        console.log('✅ 五行数据已保存:', normalizedWuxingData);

        if (normalizedWuxingData.recommendedProductIds && Array.isArray(normalizedWuxingData.recommendedProductIds)) {
          const products = ALL_PRODUCTS.filter(p =>
            normalizedWuxingData.recommendedProductIds!.includes(p.id)
          );
          setRecommendedProducts(products);
        } else {
          const products = getProductsByWuxing(normalizedWuxingData.xiYongWuXing);
          setRecommendedProducts(products);
        }

        const cleanAnswer = answer.substring(0, dataStart).trim();
        setLiteAnswer(cleanAnswer);
      } else {
        console.log('⚠️ 未找到JSON标记,使用完整报告');
        setRecommendedProducts([]);
        setInlineRecommendations([]);
        setWuxingDataState(null);
        setLiteAnswer(answer);
      }
    } catch (error) {
      console.warn('⚠️ 解析五行数据失败:', error);
      console.warn('📝 原始answer:', answer);
      setRecommendedProducts([]);
      setInlineRecommendations([]);
      setWuxingDataState(null);
      setLiteAnswer(answer);
    }
  };

  const loadHistory = async () => {
    const id = userId || getBaziLiteUserId();
    setHistoryLoading(true);
    try {
      const response = await fetch(`/api/bazi-lite-history?userId=${encodeURIComponent(id)}`);
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || '历史记录读取失败');
      setHistoryRecords(Array.isArray(payload.records) ? payload.records : []);
    } catch (error: any) {
      message.error(error.message || '历史记录读取失败');
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistoryRecord = async (recordId: string) => {
    const id = userId || getBaziLiteUserId();
    setHistoryLoading(true);
    try {
      const response = await fetch(`/api/bazi-lite-history?userId=${encodeURIComponent(id)}&recordId=${encodeURIComponent(recordId)}`);
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || '历史记录读取失败');
      const record = payload.record as BaziLiteHistoryRecord;
      setCurrentRecordId(record.recordId);
      setValidationAnswer(record.validationAnswer || '');
      setValidationFeedbacks({});
      applyLiteAnswerPayload(record.answer || '');
    } catch (error: any) {
      message.error(error.message || '历史记录读取失败');
    } finally {
      setHistoryLoading(false);
    }
  };

  const generateLiteValidation = async () => {
    if (!fourPillars || daYun.length === 0) {
      message.error(copy.toast.finishChartFirst);
      return;
    }

    if (hourUnknownSelected) {
      message.error(copy.toast.needHour);
      return;
    }

    try {
      await form.validateFields(['question']);
    } catch {
      message.error(copy.toast.fillQuestion);
      return;
    }

    const values = form.getFieldsValue();
    const { birthDateTime, city, gender, longitude, latitude, question, pastEvents } = values;
    const selectedCity = findCityByOptionValue(city);

    const birthInfo = {
      solar: birthDateTime.date.format('YYYY-MM-DD HH:mm'),
      city: selectedCity?.name || '',
      longitude,
      latitude,
      gender,
    };

    setValidationLoading(true);
    setLiteAnswer('');
    setValidationAnswer('');
    setValidationFeedbacks({});
    setCurrentRecordId('');
    try {
      const activeUserId = userId || getBaziLiteUserId();
      if (!userId) setUserId(activeUserId);
      const resp = await fetch('/api/bazi-lite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fourPillars,
          daYun,
          gender,
          locale,
          birthInfo,
          ziWeiData,
          question,
          userId: activeUserId,
          validationOnly: true,
          pastEvents: Array.isArray(pastEvents)
            ? pastEvents.filter((item: PastEventFormItem) => item?.year && item?.event?.trim())
            : [],
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || data.details || copy.toast.reportFailed);
      scrollToValidation.current = true;
      setValidationAnswer(data.answer || '');
      setCurrentRecordId(data.recordId || '');
      loadHistory().catch(() => undefined);
    } catch (error: any) {
      console.error(error);
      message.error(error.message || copy.toast.retry);
    } finally {
      setValidationLoading(false);
    }
  };

  const generateLiteReport = async () => {
    if (!fourPillars || daYun.length === 0) {
      message.error(copy.toast.finishChartFirst);
      return;
    }

    if (hourUnknownSelected) {
      message.error(copy.toast.needHour);
      return;
    }

    if (!isValidationComplete) {
      message.error('请先完成前事验证');
      return;
    }

    try {
      await form.validateFields(['question']);
    } catch {
      message.error(copy.toast.fillQuestion);
      return;
    }

    const values = form.getFieldsValue();
    const { birthDateTime, city, gender, longitude, latitude, question, pastEvents } = values;
    const selectedCity = findCityByOptionValue(city);

    const birthInfo = {
      solar: birthDateTime.date.format('YYYY-MM-DD HH:mm'),
      city: selectedCity?.name || '',
      longitude,
      latitude,
      gender,
    };

    const validationFeedback = validationItems
      .map((item, index) => `${index + 1}. ${item}：${validationFeedbacks[index] || '未反馈'}`)
      .join('\n');

    setAiLoading(true);
    setReportLoading(true);
    try {
      const activeUserId = userId || getBaziLiteUserId();
      if (!userId) setUserId(activeUserId);
      const resp = await runReportStream('/api/bazi-lite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fourPillars,
          daYun,
          gender,
          locale,
          birthInfo,
          ziWeiData,
          question,
          userId: activeUserId,
          recordId: currentRecordId,
          validationAnswer,
          validationFeedback,
          pastEvents: Array.isArray(pastEvents)
            ? pastEvents.filter((item: PastEventFormItem) => item?.year && item?.event?.trim())
            : [],
        }),
      }, (text) => { setLiteAnswer(text.split('<!--DATA_START-->')[0].trimEnd()); setAiLoading(false); });

      if (!resp.ok) {
        let errorMessage = copy.toast.reportFailed;
        try {
          const errorData = await resp.json();
          errorMessage = errorData.error || errorData.details || errorMessage;
        } catch {
          // ignore json parse error
        }
        throw new Error(errorMessage);
      }

      const data = await resp.json();
      const answer = data.answer || '暂无报告内容';
      console.log('answer0', answer);
      setCurrentRecordId(data.recordId || currentRecordId);
      applyLiteAnswerPayload(answer);
      loadHistory().catch(() => undefined);
      message.success(copy.toast.reportGenerated);
    } catch (error: any) {
      console.error(error);
      message.error(error.message || copy.toast.retry);
    } finally {
      setAiLoading(false);
      setReportLoading(false);
    }
  };

  const onReset = () => {
    form.resetFields();
    setFourPillars(null);
    setDaYun([]);
    setLunarDateStr('');
    setTrueSolarTimeStr('');
    setShiChenBoundaryHint('');
    setLiteAnswer('');
    setCurrentRecordId('');
    setValidationAnswer('');
    setValidationFeedbacks({});
    setRecommendedProducts([]);
    setInlineRecommendations([]);
    setWuxingDataState(null);
  };

  useEffect(() => {
    form.setFieldsValue({
      birthDateTime: { date: dayjs(), isLunar: false },
      gender: '',
    });
    setCityScope('domestic');
  }, [form]);

  useEffect(() => {
    setUserId(getBaziLiteUserId());
  }, []);

  useEffect(() => {
    dayjs.locale(locale === 'zh-CN' ? 'zh-cn' : 'en');
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LITE_LOCALE_STORAGE_KEY, locale);
    }
    document.title = copy.metaTitle;
  }, [copy.metaTitle, locale]);

  useEffect(()=> {
    console.log('answer0', liteAnswer)
  },[liteAnswer])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (reportRef.current) {
        reportRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [reportHasContent]);

  const reportSegments = useMemo(() => {
    return liteAnswer.split(/(\[\[PRODUCT_\d+\]\])/g).filter(Boolean);
  }, [liteAnswer]);

  const goToTopicHubPage = () => {
    navigate('/topics');
  };

  const shareSummaryData = useMemo<ShareSummaryData>(() => {
    const birthDate = form.getFieldValue('birthDateTime')?.date;
    const genderLabel = locale === 'zh-CN'
      ? (form.getFieldValue('gender') === 'male' ? '男' : '女')
      : (form.getFieldValue('gender') === 'male' ? 'Male' : 'Female');
    const birthInfo = birthDate?.format(locale === 'zh-CN' ? 'YYYY年M月D日' : 'MMM D, YYYY') || '';
    const currentYear = new Date().getFullYear();

    if (!liteAnswer.trim()) {
      return {
        gender: genderLabel,
        birthInfo,
        personalityTags: [...copy.shareFallback.personalityTags],
        yearKeywords: [...copy.shareFallback.yearKeywords],
        oneLiner: copy.shareFallback.oneLiner,
        year: currentYear,
        focusLabel: copy.shareFallback.focusLabel,
      };
    }

    return buildShareSummary(
      liteAnswer,
      genderLabel,
      birthInfo,
      currentYear,
      form.getFieldValue('question'),
      copy.shareFallback
    );
  }, [copy.shareFallback, form, liteAnswer, locale]);

  return (
    <ConfigProvider locale={locale === 'zh-CN' ? zhCN : enUS}>
      <AILoadingAnimation visible={aiLoading} />
      <div className="app-container">
        <div className="app-layout">
          <div className="center-content">
            <div style={{ width: '100%', maxWidth: '900px' }}>
              <div className="form-card lite-form-card">
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '14px' }}>
                  <div style={{ display: 'inline-flex', padding: '4px', borderRadius: '999px', background: 'rgba(212, 175, 106, 0.10)', border: '1px solid rgba(212, 175, 106, 0.16)', gap: '4px' }}>
                    <button
                      className="lite-locale-button"
                      type="button"
                      onClick={() => setLocale('zh-CN')}
                      style={{
                        border: 'none',
                        borderRadius: '999px',
                        padding: '7px 14px',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        background: locale === 'zh-CN' ? '#b8935d' : 'transparent',
                        color: locale === 'zh-CN' ? '#fff' : '#8a6a3d',
                      }}
                    >
                      {copy.language.zh}
                    </button>
                    <button
                      className="lite-locale-button"
                      type="button"
                      onClick={() => setLocale('en-US')}
                      style={{
                        border: 'none',
                        borderRadius: '999px',
                        padding: '7px 14px',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        background: locale === 'en-US' ? '#b8935d' : 'transparent',
                        color: locale === 'en-US' ? '#fff' : '#8a6a3d',
                      }}
                    >
                      {copy.language.en}
                    </button>
                  </div>
                </div>
                <div
                  className="lite-brand-block"
                  style={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    marginBottom: '18px',
                  }}
                >
                  <span
                    className="lite-brand-name"
                    style={{
                      color: '#8a6a3d',
                      fontSize: '36px',
                      lineHeight: 1.1,
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                    }}
                  >
                    {copy.brandName}
                  </span>
                  <span className="lite-brand-divider" />
                </div>
                <div
                  className="lite-brand-slogan"
                  style={{
                    maxWidth: '320px',
                    marginBottom: '28px',
                    color: '#6b7280',
                    fontSize: '15px',
                    lineHeight: 1.7,
                  }}
                >
                  {copy.slogan}
                </div>
                <div
                  className="lite-hero-intro"
                  style={{
                    marginBottom: '24px',
                    textAlign: 'center',
                  }}
                >
                  <h1
                    className="form-title"
                    style={{
                      fontSize: '30px',
                      marginBottom: '12px',
                      background: 'linear-gradient(135deg, #8a6a3d 0%, #b8935d 55%, #d4af6a 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      animation: 'none',
                    }}
                  >
                    {copy.heroTitle}
                  </h1>
                  <h2
                    className="hero-subtitle"
                    style={{
                      maxWidth: '680px',
                      margin: '0 auto',
                      color: '#5b6472',
                      fontSize: '17px',
                      lineHeight: 1.8,
                    }}
                  >
                    {copy.heroSubtitle}
                  </h2>
                </div>
                <div className="lite-extension-section" style={{ marginBottom: '22px' }}>
                  <div
                    className="lite-topic-entry"
                    style={{
                      padding: '22px 24px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #fff8e8 0%, #f4f3ff 100%)',
                      border: '1px solid rgba(184, 147, 93, 0.48)',
                      boxShadow: '0 12px 30px rgba(115, 82, 36, 0.12)',
                    }}
                  >
                    <div className="lite-topic-entry-inner" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
                        <div className="lite-topic-entry-icon" aria-hidden="true" style={{ display: 'grid', placeItems: 'center', flex: '0 0 42px', width: '42px', height: '42px', color: '#fff', background: '#8a6a3d', borderRadius: '8px', fontSize: '20px' }}>
                          <AppstoreOutlined />
                        </div>
                        <div>
                          <div style={{ fontSize: '19px', fontWeight: 700, color: '#47311d', marginBottom: '6px' }}>
                          {copy.topicEntryTitle}
                        </div>
                        <div style={{ fontSize: '14px', color: '#625c55', lineHeight: 1.75 }}>
                          {copy.topicEntryDesc}
                        </div>
                      </div>
                      </div>
                      <Button type="primary" icon={<ArrowRightOutlined />} onClick={goToTopicHubPage} className="lite-topic-entry-button" style={{ flex: '0 0 auto', height: '42px', border: 'none', background: '#8a6a3d', fontWeight: 700 }}>
                        {copy.topicEntryAction}
                      </Button>
                    </div>
                  </div>
                </div>
                <Form form={form} name="lite-form" onFinish={onFinish} layout="vertical">
                  <Form.Item
                    name="birthDateTime"
                    label={copy.form.birthTimeLabel}
                    rules={[{ required: true, message: copy.form.birthTimeRequired }]}
                  >
                    <BirthDateTimePicker locale={locale} />
                  </Form.Item>

                  <Form.Item
                    name="gender"
                    label={copy.form.genderLabel}
                    rules={[{ required: true, message: copy.form.genderRequired }]}
                  >
                    <Select
                      placeholder={copy.form.genderPlaceholder}
                      options={[
                        { value: 'male', label: copy.form.male },
                        { value: 'female', label: copy.form.female },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item
                    label={copy.form.cityLabel}
                    required
                  >
                    <div>
                      <div
                        style={{
                          display: 'inline-flex',
                          padding: '4px',
                          marginBottom: '12px',
                          borderRadius: '999px',
                          background: 'rgba(212, 175, 106, 0.10)',
                          border: '1px solid rgba(212, 175, 106, 0.16)',
                          gap: '4px',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => handleCityScopeChange('domestic')}
                          style={{
                            border: 'none',
                            borderRadius: '999px',
                            padding: '8px 18px',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            background: cityScope === 'domestic' ? '#b8935d' : 'transparent',
                            color: cityScope === 'domestic' ? '#fff' : '#8a6a3d',
                          }}
                        >
                          {copy.form.cityDomestic}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCityScopeChange('overseas')}
                          style={{
                            border: 'none',
                            borderRadius: '999px',
                            padding: '8px 18px',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            background: cityScope === 'overseas' ? '#b8935d' : 'transparent',
                            color: cityScope === 'overseas' ? '#fff' : '#8a6a3d',
                          }}
                        >
                          {copy.form.cityOverseas}
                        </button>
                      </div>

                      <Form.Item
                        name="city"
                        noStyle
                        rules={[{ required: true, message: copy.form.cityRequired }]}
                      >
                        <Select
                          showSearch
                          placeholder={cityScope === 'overseas' ? copy.form.cityOverseasPlaceholder : copy.form.cityDomesticPlaceholder}
                          options={cityOptions}
                          onChange={handleCityChange}
                          optionFilterProp="searchText"
                          filterOption={cityFilterOption}
                        />
                      </Form.Item>

                      {cityScope === 'overseas' && (
                        <div
                          style={{
                            marginTop: '8px',
                            color: '#8b8790',
                            fontSize: '12px',
                            lineHeight: 1.7,
                          }}
                        >
                          {copy.form.cityOverseasHint}
                        </div>
                      )}
                    </div>
                  </Form.Item>

                  <Form.Item
                    name="question"
                    label={copy.form.questionLabel}
                    rules={[{ required: true, message: copy.form.questionRequired }]}
                  >
                    <div className="lite-question-field">
                      <Input.TextArea
                        className="lite-question-textarea"
                        placeholder={copy.form.questionPlaceholder}
                        autoSize={{ minRows: 3, maxRows: 5 }}
                        maxLength={80}
                      />
                      <div className="lite-question-meta">
                        <span className="lite-question-extra">
                          {copy.form.questionHint}
                        </span>
                        <span className="lite-question-count">{questionValue.length}/80</span>
                      </div>
                    </div>
                  </Form.Item>

                  <Form.List name="pastEvents">
                    {(fields, { add, remove }) => (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ marginBottom: '10px', fontWeight: 600, color: '#2f2f2f' }}>
                          {copy.form.pastEventsTitle}（{copy.form.pastEventsBadge}）
                        </div>
                        <div style={{ marginBottom: '12px', color: '#8b8790', fontSize: '12px', lineHeight: 1.8 }}>
                          {copy.form.pastEventsLead} {copy.form.pastEventsLead2} {copy.form.pastEventsLead3}
                        </div>
                        <div
                          style={{
                            marginBottom: '12px',
                            padding: '12px 14px',
                            borderRadius: '12px',
                            background: 'rgba(212, 175, 106, 0.08)',
                            border: '1px solid rgba(212, 175, 106, 0.16)',
                            color: '#7b6b57',
                            fontSize: '12px',
                            lineHeight: 1.85,
                          }}
                        >
                          {copy.form.pastEventsExamplesTitle}
                          <br />
                          {copy.form.pastEventsExample1}
                          <br />
                          {copy.form.pastEventsExample2}
                          <br />
                          {copy.form.pastEventsExample3}
                        </div>
                        {fields.map(({ key, name, ...restField }) => (
                          <div
                            key={key}
                            style={{
                              marginBottom: '10px',
                              padding: '12px',
                              borderRadius: '14px',
                              border: '1px solid rgba(212, 175, 106, 0.18)',
                              background: 'rgba(255,255,255,0.72)',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '14px' }}>
                              <Form.Item
                                {...restField}
                                name={[name, 'year']}
                                rules={[{ required: true, message: copy.form.yearRequired }]}
                                style={{ marginBottom: 0, minWidth: '132px', flex: '0 0 132px' }}
                              >
                                <Select placeholder={copy.form.yearPlaceholder} options={pastEventYearOptions} />
                              </Form.Item>
                              <Button
                                type="text"
                                icon={<MinusCircleOutlined />}
                                onClick={() => remove(name)}
                                style={{ color: '#a78b63', flex: '0 0 auto' }}
                              />
                            </div>
                            <Form.Item
                              {...restField}
                              name={[name, 'event']}
                              rules={[{ required: true, message: copy.form.eventRequired }]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input.TextArea
                                placeholder={copy.form.eventPlaceholder}
                                autoSize={{ minRows: 3, maxRows: 5 }}
                                maxLength={60}
                              />
                            </Form.Item>
                          </div>
                        ))}
                        {fields.length < 3 && (
                          <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} style={{ width: '100%', borderRadius: '12px' }}>
                            {copy.form.addPastEvent}
                          </Button>
                        )}
                      </div>
                    )}
                  </Form.List>

                  {/* 隐藏字段：经度值由城市选择自动填充 */}
                  <Form.Item name="longitude" hidden>
                    <input type="number" />
                  </Form.Item>

                  {/* 早晚子时区分选项 */}
                  <Form.Item
                    name="distinguishZiShi"
                    valuePropName="checked"
                    style={{ marginBottom: '24px' }}
                  >
                    <Checkbox>
                      <span style={{ fontSize: '15px', color: '#333', marginRight: '6px' }}>
                        {copy.form.distinguishZiShi}
                      </span>
                      <Tooltip
                        title={
                          <div style={{ lineHeight: '1.8', fontSize: '13px' }}>
                            <div style={{ marginBottom: '8px', fontWeight: 600, color: '#ffd666' }}>
                              {copy.form.ziShiTooltipTitle}
                            </div>
                            <div style={{ marginBottom: '6px' }}>
                              <strong>🔲 {copy.form.ziShiTraditional}</strong>
                            </div>
                            <div style={{ marginLeft: '16px', marginBottom: '10px', color: '#d9d9d9' }}>
                              {copy.form.ziShiTraditionalDesc}
                            </div>
                            <div style={{ marginBottom: '6px' }}>
                              <strong>☑️ {copy.form.ziShiModern}</strong>
                            </div>
                            <div style={{ marginLeft: '16px', color: '#d9d9d9' }}>
                              • {copy.form.ziShiModernDesc1}<br/>
                              • {copy.form.ziShiModernDesc2}
                            </div>
                          </div>
                        }
                        overlayStyle={{ maxWidth: '320px' }}
                      >
                        <QuestionCircleOutlined style={{ color: '#d4af6a', cursor: 'help' }} />
                      </Tooltip>
                    </Checkbox>
                  </Form.Item>

                  <div className="lite-form-actions" style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                    <Button type="primary" htmlType="submit" className="submit-button">
                      {copy.form.generateBasic}
                    </Button>
                    <Button htmlType="button" onClick={onReset} className="reset-button">
                      {copy.form.reset}
                    </Button>
                  </div>
                  <div
                    style={{
                      marginTop: '14px',
                      color: '#8b8790',
                      fontSize: '12px',
                      lineHeight: 1.8,
                      textAlign: 'center',
                    }}
                  >
                    {copy.form.disclaimer}
                  </div>
                </Form>

                <section className="lite-history-panel">
                  <div>
                    <h3>本机历史测试记录</h3>
                    <p>记录绑定当前浏览器，每台设备最多保留 10 条；换设备、换浏览器或清理浏览器数据后可能看不到。</p>
                  </div>
                  <Button htmlType="button" loading={historyLoading} onClick={loadHistory}>
                    查看历史
                  </Button>
                  {historyRecords.length > 0 && (
                    <div className="lite-history-list">
                      {historyRecords.map((record) => (
                        <button key={record.recordId} className="lite-history-item" type="button" onClick={() => openHistoryRecord(record.recordId)}>
                          <strong>{record.birthSolar || '未记录出生时间'} · {record.cityName || '未知地点'}</strong>
                          <span>{record.question || '未填写具体问题'}</span>
                          <em>{new Date(record.createdAt).toLocaleString()}</em>
                        </button>
                      ))}
                    </div>
                  )}
                </section>
              </div>

              {fourPillars && (
                <div className="result-card" style={{ marginTop: '24px' }}>
                  <h2 className="result-title">{copy.baseProfile.title}</h2>
                  <div style={{ textAlign: 'center', marginBottom: 20, color: '#666' }}>
                    <p>
                      {copy.baseProfile.solarLabel}：
                      {form.getFieldValue('birthDateTime')?.date?.format(locale === 'zh-CN' ? 'YYYY年MM月DD日' : 'YYYY-MM-DD')}
                      {form.getFieldValue('birthDateTime')?.hourUnknown
                        ? ` ${copy.baseProfile.unknownHour}`
                        : ` ${form.getFieldValue('birthDateTime')?.date?.format('HH:mm')}`}
                    </p>
                    <p style={{ color: '#d4af6a', fontWeight: 500, fontSize: '14px', margin: '8px 0' }}>{copy.baseProfile.trueSolarLabel}：{trueSolarTimeStr}</p>
                    {shiChenBoundaryHint && (
                      <div style={{
                        margin: '10px auto 0',
                        maxWidth: '720px',
                        padding: '10px 14px',
                        borderRadius: '10px',
                        background: 'rgba(212, 175, 106, 0.12)',
                        border: '1px solid rgba(212, 175, 106, 0.28)',
                        color: '#8a6a3f',
                        fontSize: '13px',
                        lineHeight: 1.7,
                      }}>
                        {shiChenBoundaryHint}
                      </div>
                    )}
                    <p>{copy.baseProfile.lunarLabel}：{lunarDateStr}</p>
                  </div>
                  <FourPillars {...fourPillars} locale={locale} />

                  <div style={{ marginTop: '24px', textAlign: 'center' }}>
                    <Button
                      type="primary"
                      size="large"
                      className="submit-button"
                      loading={validationLoading}
                      onClick={generateLiteValidation}
                      disabled={hourUnknownSelected}
                    >
                      先生成前事验证
                    </Button>
                    {hourUnknownSelected && (
                      <div
                        style={{
                          marginTop: '12px',
                          maxWidth: '560px',
                          marginLeft: 'auto',
                          marginRight: 'auto',
                          color: '#8b6f47',
                          fontSize: '13px',
                          lineHeight: 1.75,
                        }}
                      >
                        {copy.baseProfile.hourRequiredHint}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {validationAnswer && !liteAnswer && (
                <section ref={validationRef} className="result-card lite-validation-card" style={{ marginTop: '24px', scrollMarginTop: '24px' }}>
                  <h2 className="result-title">前事验证</h2>
                  <p className="lite-validation-intro">请逐条判断是否贴近，系统会带着你的反馈生成完整报告。</p>
                  {validationItems.length > 0 ? (
                    <div className="lite-validation-list">
                      {validationItems.map((item, index) => (
                        <article key={`${index}-${item}`} className="lite-validation-item">
                          <div className="lite-validation-question"><span>{index + 1}.</span>{item}</div>
                          <Radio.Group
                            className="lite-validation-options"
                            optionType="button"
                            buttonStyle="solid"
                            value={validationFeedbacks[index]}
                            onChange={(event) => setValidationFeedbacks((previous) => ({ ...previous, [index]: event.target.value }))}
                            options={[
                              { label: '符合', value: '符合' },
                              { label: '部分符合', value: '部分符合' },
                              { label: '不符合', value: '不符合' },
                            ]}
                          />
                        </article>
                      ))}
                    </div>
                  ) : (
                    <div className="markdown-content">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{validationAnswer}</ReactMarkdown>
                    </div>
                  )}
                  <Button
                    type="primary"
                    size="large"
                    className="submit-button lite-validation-submit"
                    loading={reportLoading}
                    disabled={!isValidationComplete || reportStreaming}
                    onClick={generateLiteReport}
                  >
                    根据验证结果生成完整报告
                  </Button>
                </section>
              )}

              <StreamingStatus active={reportStreaming && Boolean(liteAnswer)} error={streamError} />
              {liteAnswer && (
                <div ref={reportRef} className="result-card" style={{ marginTop: '24px', marginBottom: '24px' }}>
                  <div className="lite-report-content">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <h2 className="result-title" style={{ margin: 0 }}>{copy.report.title}</h2>
                      <Button
                        type="primary"
                        disabled={reportStreaming || Boolean(streamError)}
                        onClick={() =>
                          exportImage('.lite-report-content', {
                            beforeCapture: async () => {
                              setIsExportingReport(true);
                              await new Promise((resolve) => setTimeout(resolve, 80));
                            },
                            afterCapture: async () => {
                              setIsExportingReport(false);
                            },
                          })
                        }
                        style={{
                          background: 'linear-gradient(135deg, #d4af6a 0%, #b8935d 100%)',
                          border: 'none',
                          fontWeight: 600
                        }}
                      >
                        {copy.report.save}
                      </Button>
                    </div>

                    {/* 保存提示 */}
                    <div style={{
                      margin: '0 0 20px',
                      padding: '14px 18px',
                      background: 'linear-gradient(135deg, rgba(212, 175, 106, 0.15) 0%, rgba(212, 175, 106, 0.08) 100%)',
                      borderRadius: '10px',
                      border: '1px solid rgba(212, 175, 106, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{ fontSize: '24px' }}>💡</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '15px', fontWeight: 600, color: '#d4af6a', marginBottom: '4px' }}>
                          {copy.report.tipTitle}
                        </div>
                        <div style={{ fontSize: '13px', color: '#666' }}>
                          {copy.report.tipDesc}
                        </div>
                      </div>
                    </div>

                    {wuxingDataState && (
                      <div
                        style={{
                          margin: '0 0 20px',
                          padding: '16px 18px',
                          borderRadius: '14px',
                          background: 'linear-gradient(135deg, rgba(212, 175, 106, 0.12) 0%, rgba(255, 255, 255, 0.95) 100%)',
                          border: '1px solid rgba(212, 175, 106, 0.22)',
                        }}
                      >
                        <div style={{ fontSize: '16px', fontWeight: 700, color: '#7a5630', marginBottom: '10px' }}>
                          {copy.report.wuxingTitle}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
                          <span style={{ padding: '6px 10px', borderRadius: '999px', background: 'rgba(214, 91, 61, 0.12)', color: '#a2442d', fontSize: '13px', fontWeight: 700 }}>
                            {locale === 'zh-CN' ? '主用' : 'Primary'}：{wuxingDataState.xiYongWuXing}
                          </span>
                          {Array.isArray(wuxingDataState.secondaryXiYongWuXing) && wuxingDataState.secondaryXiYongWuXing.length > 0 && (
                            <span style={{ padding: '6px 10px', borderRadius: '999px', background: 'rgba(184, 147, 93, 0.14)', color: '#8a6a3d', fontSize: '13px', fontWeight: 700 }}>
                              {locale === 'zh-CN' ? '辅用' : 'Secondary'}：{wuxingDataState.secondaryXiYongWuXing.join('、')}
                            </span>
                          )}
                          {Array.isArray(wuxingDataState.avoidWuXing) && wuxingDataState.avoidWuXing.length > 0 && (
                            <span style={{ padding: '6px 10px', borderRadius: '999px', background: 'rgba(96, 105, 117, 0.10)', color: '#606975', fontSize: '13px', fontWeight: 700 }}>
                              {locale === 'zh-CN' ? '慎用' : 'Use Carefully'}：{wuxingDataState.avoidWuXing.join('、')}
                            </span>
                          )}
                          {Array.isArray(wuxingDataState.conditionalWuXing) && wuxingDataState.conditionalWuXing.length > 0 && (
                            <span style={{ padding: '6px 10px', borderRadius: '999px', background: 'rgba(95, 111, 255, 0.10)', color: '#4c5fc1', fontSize: '13px', fontWeight: 700 }}>
                              {locale === 'zh-CN' ? '条件可用' : 'Conditional'}：{wuxingDataState.conditionalWuXing.map((item) => item.element).join('、')}
                            </span>
                          )}
                        </div>
                        {wuxingRecommendationBase && (
                          <div style={{ marginBottom: '8px', color: '#8a6a3d', fontSize: '12px', lineHeight: 1.7, fontWeight: 600 }}>
                            {locale === 'zh-CN'
                              ? `本次商品推荐按主用/辅用（${wuxingRecommendationBase}）筛选；条件可用只作说明，不默认参与选品。`
                              : `Recommendations are anchored to primary/secondary elements (${wuxingRecommendationBase}); conditional elements are explanatory only.`}
                          </div>
                        )}
                        {Array.isArray(wuxingDataState.conditionalWuXing) && wuxingDataState.conditionalWuXing.length > 0 && (
                          <div style={{ marginBottom: '8px', color: '#6d6a71', fontSize: '12px', lineHeight: 1.75 }}>
                            {wuxingDataState.conditionalWuXing.map((item) => `${item.element}：${item.condition}`).join('；')}
                          </div>
                        )}
                        <div style={{ color: '#6d6a71', fontSize: '13px', lineHeight: 1.8 }}>
                          {wuxingDataState.verificationSummary || copy.report.wuxingFallback}
                        </div>
                      </div>
                    )}

                    <div className="markdown-content" style={{ color: '#2f2f2f' }}>
                      {reportSegments.map((segment, index) => {
                        const markerMatch = segment.match(/^\[\[(PRODUCT_\d+)\]\]$/);
                        if (markerMatch) {
                          const slot = markerMatch[1];
                          const meta = inlineRecommendations.find((item) => item.slot === slot);
                          return renderInlineProductCard(meta, recommendedProducts, `${slot}-${index}`, isExportingReport, copy.report);
                        }

                        return (
                          <ReactMarkdown key={`markdown-${index}`} remarkPlugins={[remarkGfm]}>
                            {segment}
                          </ReactMarkdown>
                        );
                      })}
                    </div>
                    {!isExportingReport && <section
                      className="lite-topic-footer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '20px',
                        margin: '30px 0 0',
                        padding: '24px',
                        border: '2px solid #e2b55e',
                        borderRadius: '8px',
                        background: '#fffdf8',
                        boxShadow: '0 14px 32px rgba(41, 25, 82, 0.18)',
                      }}
                    >
                      <div>
                        <div style={{ color: '#8a6a3d', fontSize: '12px', fontWeight: 800, letterSpacing: '0.08em', marginBottom: '8px' }}>
                          {locale === 'zh-CN' ? '下一步' : 'NEXT STEP'}
                        </div>
                        <div style={{ color: '#30244f', fontSize: '21px', fontWeight: 800, lineHeight: 1.35, marginBottom: '7px' }}>
                          {locale === 'zh-CN' ? '选择一个专题，继续深入' : 'Choose a topic to go deeper'}
                        </div>
                        <div style={{ color: '#625d70', fontSize: '14px', lineHeight: 1.7 }}>
                          {locale === 'zh-CN' ? '事业、关系、孩子天赋和人生发展，都可以单独展开。' : 'Career, relationships, children’s talents, and life development are ready to explore.'}
                        </div>
                      </div>
                      <Button
                        type="primary"
                        icon={<ArrowRightOutlined />}
                        onClick={goToTopicHubPage}
                        style={{
                          flex: '0 0 auto',
                          height: '48px',
                          border: 'none',
                          background: 'linear-gradient(135deg, #d4af6a 0%, #b8935d 100%)',
                          boxShadow: '0 8px 18px rgba(184, 147, 93, 0.28)',
                          fontWeight: 700,
                          padding: '0 20px',
                        }}
                      >
                        {locale === 'zh-CN' ? '进入专题中心' : 'Browse topics'}
                      </Button>
                    </section>}
                  </div>
                </div>
              )}

              {/* 分享按钮区域 */}
              {liteAnswer && (
                <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <Button
                    disabled={reportStreaming || Boolean(streamError)}
                    onClick={() =>
                      exportImage('.lite-report-content', {
                        beforeCapture: async () => {
                          setIsExportingReport(true);
                          await new Promise((resolve) => setTimeout(resolve, 80));
                        },
                        afterCapture: async () => {
                          setIsExportingReport(false);
                        },
                      })
                    }
                    style={{
                      background: 'linear-gradient(135deg, #d4af6a 0%, #b8935d 100%)',
                      border: 'none',
                      color: '#fff',
                      fontWeight: 600,
                      height: '44px',
                      padding: '0 24px'
                    }}
                  >
                    {copy.report.saveFull}
                  </Button>
                  <Button
                    disabled={reportStreaming || Boolean(streamError)} onClick={() => setShowShareCard(true)}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      color: '#fff',
                      fontWeight: 600,
                      height: '44px',
                      padding: '0 24px'
                    }}
                  >
                    {copy.report.share}
                  </Button>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
      <ShareCard
        visible={showShareCard}
        onClose={() => setShowShareCard(false)}
        locale={locale}
        copy={copy.shareCard}
        summaryData={shareSummaryData}
      />
    </ConfigProvider>
  );
};

export default LitePage;
